// react
import { ReactNode, useEffect, useMemo, useState } from 'react';

// types
import { Option } from '../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import {
	Checkbox,
	FormControl,
	FormHelperText,
	InputAdornment,
	InputLabel,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select as MuiSelect,
	SelectChangeEvent,
	SelectProps as MuiSelectProps,
	Grid,
} from '@mui/material';

// ui
import { IconCircle, IconCircleProps } from '../../ui/IconCircle/IconCircle';

// props
export type SelectProps = {
	IconCircleProps?: IconCircleProps;
	hasAllOption?: boolean;
	hasNoneOption?: boolean;
	hasSearchParam?: boolean;
	helperText?: string;
	options?: Option[];
} & MuiSelectProps;

export const Select = ({
	defaultValue,
	disabled,
	fullWidth = true,
	helperText,
	hasAllOption = false,
	hasNoneOption = true,
	hasSearchParam = false,
	IconCircleProps,
	id,
	label,
	multiple = false,
	name,
	onChange,
	options = [],
	required,
	size = 'small',
	value,
	variant = 'outlined',
	...props
}: SelectProps) => {
	// hooks
	const { t } = useTranslation();

	// add none option (create local copy to avoid mutating the prop)
	const optionsList =
		!required && hasNoneOption && !multiple && !options.find((o) => o.label === t('none'))
			? [{ label: t('none'), value: '' }, ...options]
			: options;

	// defaults
	const defaultSelected = useMemo(() => {
		let valueNew: unknown = multiple ? [] : '';

		if (defaultValue) {
			valueNew = defaultValue;
		} else if (value) {
			valueNew = value;
		}

		return valueNew;
	}, [defaultValue, multiple, value]);

	// state
	const [searchParams, setSearchParams] = useSearchParams();
	const [selected, setSelected] = useState<unknown | unknown[]>(defaultSelected);

	// vars
	const isSelectedArray = Array.isArray(selected);
	const isAllOptionsDisabled = optionsList.filter((o) => o.disabled).length === optionsList.length;
	const isAllSelected =
		isSelectedArray && optionsList.length > 0 && selected.length === optionsList.length;
	const optionsEnabled = optionsList.filter((option) => !option.disabled);

	// handlers
	const handleChange = (e: SelectChangeEvent<unknown>, child: ReactNode) => {
		const { value } = e.target;
		const param = String(name);

		let selectedNew;

		// if value is array
		if (Array.isArray(value)) {
			const isValueAll = value[value.length - 1] === 'all';

			selectedNew = value;

			if (isValueAll) {
				// check or uncheck all enabled options
				if (isSelectedArray && selected.length === optionsEnabled.length) {
					selectedNew = [];
				} else {
					selectedNew = optionsEnabled.map((option) => option.value);
				}
			}
		} else {
			selectedNew = value as string;
		}

		if (hasSearchParam) {
			// if value is array
			if (Array.isArray(selectedNew)) {
				// deduplicate values
				const uniqueValues = [...new Set(selectedNew)];

				if (uniqueValues.length === 0) {
					// delete search param
					searchParams.delete(param);
				} else {
					// set search param as comma separated list
					searchParams.set(param, uniqueValues.join(','));
				}
			} else if (selectedNew === 'all' || selectedNew === '') {
				// delete search param
				searchParams.delete(param);
			} else {
				// set search param
				searchParams.set(param, String(selectedNew));
			}

			// reset page to 1
			searchParams.delete('page');

			// set search params
			setSearchParams(searchParams);
		}

		// set selected
		if (!hasSearchParam) {
			setSelected(selectedNew);
		}

		// redefine value as selected
		e.target.value = selectedNew;
		if (onChange) {
			onChange(e, child);
		}
	};

	// render
	const renderValue = (val: unknown) => {
		// if value is array
		if (Array.isArray(selected)) {
			if (hasAllOption && isAllSelected) {
				return t('all');
			}

			// create array of labels using values
			const labels = [];
			// for each value
			for (let v = 0; v < selected.length; v++) {
				// find option
				const option = optionsList.find((o) => o.value === selected[v]);

				// add option
				if (option) {
					let labelText: string;
					if (typeof option.label === 'string') {
						labelText = option.label;
					} else if (option?.MenuItemProps?.resource?.name) {
						labelText = option?.MenuItemProps?.resource?.name;
					} else {
						labelText = String(option.value);
					}
					labels.push(labelText);
				}
			}
			return labels.join(', ');
		} else {
			if (selected === 'all') {
				return t('all');
			}

			// find option
			const option = optionsList.find((o) => o.value === val);

			// set value as option label
			if (option) {
				return (
					<Grid alignItems="center" container spacing={1}>
						{option.icon && <Grid item>{option.icon}</Grid>}
						<Grid item sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
							{option.label}
						</Grid>
					</Grid>
				);
			}

			return val;
		}
	};

	// define conditional props
	const selectProps: MuiSelectProps = {};

	if (IconCircleProps) {
		selectProps.startAdornment = (
			<InputAdornment position="start" sx={{ marginRight: `6px !important` }}>
				<IconCircle size="xs" {...IconCircleProps} />
			</InputAdornment>
		);
	}

	let val: unknown = multiple ? [] : '';

	if (value !== undefined) {
		if (multiple) {
			val = value;
		} else if (value === 'all' || Boolean(optionsList.find((option) => option.value === value))) {
			val = value;
		}
	} else {
		val = selected;
	}

	// set selected when value changes
	useEffect(() => {
		if (value !== undefined) {
			setSelected(value);
		}
	}, [value]);

	return (
		<FormControl sx={{ width: '100%' }}>
			{label && (
				<InputLabel
					disabled={disabled}
					htmlFor={id}
					required={required}
					shrink={false}
					sx={{ position: 'static', transform: 'none' }}>
					{label}
				</InputLabel>
			)}

			<MuiSelect
				defaultValue={defaultValue}
				disabled={disabled}
				displayEmpty={true}
				fullWidth={fullWidth}
				id={id}
				multiple={multiple}
				name={name}
				onChange={handleChange}
				renderValue={renderValue}
				required={required}
				size={size}
				value={val}
				variant={variant}
				{...selectProps}
				{...props}>
				{hasAllOption && (
					<MenuItem dense={true} disabled={isAllOptionsDisabled} disableRipple={true} value="all">
						{multiple && (
							<Checkbox
								checked={isAllSelected}
								indeterminate={
									isSelectedArray && selected.length > 0 && selected.length < optionsList.length
								}
								disabled={disabled}
							/>
						)}
						<ListItemText
							primary={t('all')}
							primaryTypographyProps={{
								color: 'brand.grey.800',
							}}
						/>
					</MenuItem>
				)}
				{optionsList.map((option, i) => {
					const { disabled, icon, label, MenuItemProps = {}, primary, secondary, value } = option;
					const currentValue = (val as unknown[]) || [];
					return (
						<MenuItem
							dense={true}
							disabled={disabled}
							disableRipple={true}
							key={`menu-item-${value}`}
							value={value}
							{...MenuItemProps}>
							{multiple && (
								<Checkbox
									checked={currentValue.some((s) => String(s) === String(value))}
									disabled={disabled}
								/>
							)}
							{icon && <ListItemIcon>{icon}</ListItemIcon>}
							<ListItemText
								primary={primary || label}
								primaryTypographyProps={{
									color: 'brand.grey.800',
								}}
								secondary={secondary}
								secondaryTypographyProps={{
									color: 'brand.grey.500',
									variant: 'caption',
								}}
							/>
						</MenuItem>
					);
				})}
			</MuiSelect>
			{helperText && <FormHelperText>{helperText}</FormHelperText>}
		</FormControl>
	);
};
